<template>
  <div class="row">
    <div class="col-12 col-pt-list">
      <hr class="hr-dark-color hr-thick" />
      <p class="loading" v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i> loading patients...</p>
      <list-errors :errors="errors" />

      <div class="form-row" v-if="!loading">
        <div class="col-6 col-sm-4 col-xl-2">
          <label for="patientNameFilter" class="small mb-1">Search by name</label>
          <input
            id="patientNameFilter"
            v-model="patientNameFilter"
            class="form-control"
            type="search"
            @input="onClientFilterChange"
          />
        </div>
        <div class="col-6 col-sm-4 col-xl-2">
          <label for="scheduledFilter" class="small mb-1">Scheduled?</label>
          <select
            id="scheduledFilter"
            class="form-control"
            v-model="selectedScheduledFilter"
            @change="onScheduledFilterChange"
          >
            <option value="all">All</option>
            <option value="yes">Scheduled</option>
            <option value="no">Not scheduled</option>
          </select>
        </div>
        <div class="col-6 col-sm-4 col-xl-2">
          <label for="lastAppointmentDateFilter" class="small mb-1">Date of last appointment</label>
          <select
            id="lastAppointmentDateFilter"
            v-model="selectedLastAppointmentDateFilter"
            class="form-control"
            @change="onClientFilterChange"
          >
            <option value="all">All dates</option>
            <option v-for="option in dateFilterOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="col-6 col-sm-4 col-xl-2">
          <label for="lastContactedDateFilter" class="small mb-1">Date last contacted</label>
          <select
            id="lastContactedDateFilter"
            v-model="selectedLastContactedDateFilter"
            class="form-control"
            @change="onClientFilterChange"
          >
            <option value="all">All dates</option>
            <option v-for="option in dateFilterOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="col-6 col-sm-4 col-xl-2">
          <label for="stageFilter" class="small mb-1">Path to Health Stage</label>
          <select
            id="stageFilter"
            class="form-control"
            v-model="selectedStageFilter"
            @change="onStageFilterChange"
          >
            <option value="all">All stages</option>
            <option v-for="stage in stageFilterOptions" :key="stage" :value="String(stage)">
              {{ stage}}: {{ getStageName(stage) }}
            </option>
          </select>
        </div>
        <div class="col-6 col-sm-4 col-xl-2">
          <label for="assignedTeamMemberFilter" class="small mb-1">Assigned to</label>
          <team-member-select
            id="assignedTeamMemberFilter"
            v-model="selectedAssignedTeamMemberId"
            :allow-all="true"
            all-label="All team members"
          />
        </div>
      </div> <!-- /.form-row -->

      <p class="h-scroll-indicator" v-if="!loading">scroll right <i class="fas fa-arrow-right"></i></p>

      <div class="table-responsive" v-if="!loading">
        <div class="patient-list-container">
          <div class="patient-list-header d-flex justify-content-between">
            <span v-if="showBatchAssign" class="patient-list-cell patient-list-checkbox-cell">
              <input
                type="checkbox"
                aria-label="Select all visible patients"
                :checked="allVisiblePatientsSelected"
                @change="toggleAllVisiblePatients"
              />
            </span>
            <div class="patient-list-thead d-flex justify-content-between">
              <span class="col-1 patient-list-cell">Priority</span>
              <span class="col-3 patient-list-cell">Patient Name</span>
              <span class="col-2 patient-list-cell">Path to Health</span>
              <span class="col-1 patient-list-cell">Scheduled</span>
              <span class="col patient-list-cell">
                {{ patientListMode === 'snoozed' ? 'Snoozed Until' : patientListMode === 'completed' ? 'Completed' : 'Last Contacted' }}
              </span>
              <span class="col patient-list-cell">Assigned To</span>
            </div>
          </div>
          <div v-for="patient in patients" :key="patient.id" class="patient-list-row d-flex justify-content-between">
            <span v-if="showBatchAssign" class="patient-list-cell patient-list-checkbox-cell">
              <input
                type="checkbox"
                :aria-label="`Select ${patient.patientName || 'patient'}`"
                :value="patient.id"
                v-model="selectedPatientIds"
                @click.stop
              />
            </span>
            <button class="btn btn-patient-list d-flex justify-content-between align-items-center" type="button" @click="goToPatientDetail(patient)">
              <span v-if="patient.priorityScore || patient.priorityScore==0" class="col-1 patient-list-cell">
                <span class="badge badge-custom badge-custom-center" :class="`badge-custom-${getPriorityColor(patient.priorityScore)}`">
                  {{ Math.round(patient.priorityScore)}}
                </span>
              </span>
              <span v-else class="col-1 patient-list-cell"><em><small>TBD</small></em></span>
              <span class="col-3 patient-list-cell">{{ patient.patientName || '(No Name)' }}</span>
              <span class="col-2 patient-list-cell">
                <svg v-if="patient.stage" class="stage-icon" :width="patient.stage==6 ? '26px' : '29px'" height="26px" :viewBox="patient.stage==6 ? '0 0 50 50' : '0 0 65.091 50'" :style="{ fill: getStageColor(patient.stage) }" xmlns="http://www.w3.org/2000/svg">
                  <path v-if="patient.stage !== 6" d="M65.091,25,47.844,7.752v7.077a25,25,0,1,0,0,20.341v7.077Z" transform="translate(0 0.001)"/>
                  <circle v-else cx="25" cy="25" r="25" />
                </svg>
                <span v-if="patient.stage">{{ patient.stage}}</span>
                <span v-else><em><small>TBD</small></em></span>
              </span>
              <span v-if="patient.pathToHealthStageIsScheduled === true" class="col-1 patient-list-cell">Yes</span>
              <span v-else-if="patient.pathToHealthStageIsScheduled === false" class="col-1 patient-list-cell">No</span>
              <span v-else class="col-1 patient-list-cell"><em><small>TBD</small></em></span>
              <span v-if="patientListMode === 'snoozed' && patient.snoozedUntil" class="col patient-list-cell">{{ patient.snoozedUntil | prettyDateTimeShortTz }}</span>
              <span v-else-if="patientListMode === 'completed' && patient.completedAt" class="col patient-list-cell">{{ patient.completedAt | prettyDateTimeShortTz }}</span>
              <span v-else-if="patient.lastContactedDate" class="col patient-list-cell">{{ patient.lastContactedDate | prettyDateTimeShortTz }}</span>
              <span v-else class="col patient-list-cell"><em><small>N/A</small></em></span>
              <pt-active-case-assigned :patient="patient" class="col patient-list-cell" />
            </button>
          </div> <!-- /.patient-list-row -->
          <div v-if="!patients.length" class="patient-list-empty">
            <em>No patients found.</em>
          </div>
        </div>  <!-- /.patient-list-container -->
      </div> <!-- /.table-responsive-->

      <div class="row row-table-footer align-items-end" v-if="!loading">
        <div class="col-6">
          <div v-if="showBatchAssign && selectedPatientIds.length" class="batch-assign-form">
            <p>Assign {{ selectedPatientIds.length }} selected patient{{ selectedPatientIds.length === 1 ? '' : 's' }} to:</p>
            <div class="d-flex justify-content-start align-items-center">
              <div class="flex-grow-1">
                <team-member-select
                  id="batchAssignedTeamMember"
                  v-model="batchAssignedTeamMemberId"
                />
              </div>
              <div>
                <button
                  class="btn btn-green btn-icon-left"
                  type="button"
                  :disabled="!batchAssignedTeamMemberId || batchAssigning"
                  @click="batchAssignPatients"
                >
                  <i v-if="batchAssigning" class="far fa-fw fa-pulse fa-spinner"></i>
                  <i v-if="!batchAssigning" class="fas fa-fw fa-check"></i>
                  <span v-if="!batchAssigning">Save</span>
                </button>
              </div>
            </div>
          </div>
        </div> <!-- /.col-6 -->
        <div class="col-6 text-right">
          <button @click="downloadCSV" class="btn btn-link btn-icon-right">Download CSV<i class="far fa-fw fa-file-download"></i></button>
        </div> <!-- /.col-6 -->
      </div> <!-- /.row-table-footer -->

      <div class="d-flex justify-content-between align-items-center" v-if="!loading">
        <button class="btn btn-light-outline btn-icon-left" :disabled="pageNumber <= 1" @click="loadPreviousPage">
          <i class="far fa-angle-left"></i>Previous
        </button>
        <p class="snug">Page {{ pageNumber }}</p>
        <button class="btn btn-light-outline btn-icon-right" :disabled="!hasNextPage" @click="loadNextPage">
          Next<i class="far fa-angle-right"></i>
        </button>
      </div>

    </div> <!-- /.col-12 -->
  </div> <!-- /.row -->
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import moment from 'moment-timezone'
import ListErrors from '../components/ListErrors.vue'
import ptActiveCaseAssigned from '../components/ptActiveCaseAssigned.vue'
import TeamMemberSelect from '../components/TeamMemberSelect.vue'

const PAGE_SIZE = 100

export default {
  components: {
    ListErrors,
    ptActiveCaseAssigned,
    TeamMemberSelect,
  },
  data() {
    return {
      loading: false,
      errors: [],
      patients: [],
      allPatients: [],
      pageNumber: 1,
      hasNextPage: false,
      // Placeholder: switch this to "priorityScore" when the field is available in Firestore.
      sortMode: 'priorityScore',
      // pre-applied from the ?stage= query param (e.g. linked from the Path to Health stats page)
      selectedStageFilter: this.initialStageFilterFromRoute(),
      selectedScheduledFilter: 'all',
      patientNameFilter: '',
      selectedLastAppointmentDateFilter: 'all',
      selectedLastContactedDateFilter: 'all',
      selectedAssignedTeamMemberId: '',
      selectedPatientIds: [],
      batchAssignedTeamMemberId: '',
      batchAssigning: false,
      stageFilterOptions: [1, 2, 3, 4, 5, 6],
      dateFilterOptions: [
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'oneWeek', label: 'Within 1 week' },
        { value: 'oneMonth', label: 'Within 1 month' },
        { value: 'sixMonths', label: 'Within 6 months' },
        { value: 'oneYear', label: 'Within 1 year' },
        { value: 'twoYears', label: 'Within 2 years' },
      ],
      stageNames: {
        1: 'Emergency Relief',
        2: 'Understand Each Other Fully',
        3: 'Make a Plan Together',
        4: 'Get Active Disease Under Control',
        5: 'Rebuild for the Long Run',
        6: 'Stay Healthy Together',
      },
    }
  },
  computed: {
    ...mapGetters(['custom_claims']),
    customerId() {
      return this.custom_claims?.customerId || null
    },
    locationId() {
      return this.custom_claims?.authorized_location || null
    },
    patientListMode() {
      return this.$route.meta.patientListMode || 'active'
    },
    showBatchAssign() {
      return this.patientListMode !== 'completed'
    },
    allVisiblePatientsSelected() {
      return this.patients.length > 0 && this.patients.every((patient) => this.selectedPatientIds.includes(patient.id))
    },
    filteredPatients() {
      const wantScheduled = this.selectedScheduledFilter === 'yes'
      const nameSearch = this.patientNameFilter.trim().toLocaleLowerCase()

      return this.allPatients.filter((patient) => {
        if (!this.matchesPatientListMode(patient)) {
          return false
        }
        if (this.selectedScheduledFilter !== 'all' && patient.pathToHealthStageIsScheduled !== wantScheduled) {
          return false
        }
        if (nameSearch && !patient.patientName.toLocaleLowerCase().includes(nameSearch)) {
          return false
        }
        if (!this.matchesDateFilter(patient.mostRecentAppointmentDate, this.selectedLastAppointmentDateFilter)) {
          return false
        }
        if (!this.matchesDateFilter(patient.lastContactedDate, this.selectedLastContactedDateFilter)) {
          return false
        }
        if (this.selectedAssignedTeamMemberId && patient.activeCaseAssignedToId !== this.selectedAssignedTeamMemberId) {
          return false
        }
        return true
      })
    },
  },
  watch: {
    custom_claims(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetAndLoad()
      }
    },
    selectedAssignedTeamMemberId() {
      this.onAssignedTeamMemberChange()
    },
    patientListMode() {
      if (this.patientListMode === 'all') {
        this.selectedStageFilter = 'all'
        this.selectedScheduledFilter = 'all'
        this.patientNameFilter = ''
        this.selectedLastAppointmentDateFilter = 'all'
        this.selectedLastContactedDateFilter = 'all'
        this.selectedAssignedTeamMemberId = ''
      }
      this.clearBatchSelection()
      this.resetAndLoad()
    },
  },
  methods: {
    initialStageFilterFromRoute() {
      if (this.$route.meta.patientListMode === 'all') {
        return 'all'
      }

      const stageNum = Number(this.$route.query.stage)
      // stageFilterOptions isn't set on `this` yet at data() time, so check the valid range directly
      return Number.isInteger(stageNum) && stageNum >= 1 && stageNum <= 6 ? String(stageNum) : 'all'
    },
    getPriorityColor(score) {
      if (score === null || score === undefined) {
        return 'dark'
      }
      if (score >= 15) {
        return 'salmon'
      } else if (score >= 10) {
        return 'orange'
      } else if (score >= 5) {
        return 'yellow'
      } else if (score >= 2) {
        return 'blue'
      } else {
        return 'green'
      }
    },
    getStageName(stage) {
      if (stage === null || stage === undefined) {
        return null
      }
      return this.stageNames[stage] || `Stage ${stage}`
    },
    getStageColor (stage) {
      if (stage === null || stage === undefined) {
        return '#e7e9eb' // @dim-color
      }
      switch (stage) {
        case 1:
          return '#EE404A' // @color-renken-red
        case 2:
          return '#F58915' // @color-renken-orange
        case 3:
          return '#F6DF4E' // @color-renken-yellow
        case 4:
          return '#8DAD3C' // @color-renken-green
        case 5:
          return '#6B86B2' // @color-renken-blue
        case 6:
          return '#B63E97' // @color-renken-magenta
        default:
          return '#e7e9eb' // @dim-color for unknown stages
      }
    },
    patientsCollectionRef() {
      if (!this.customerId || !this.locationId) {
        return null
      }

      return Vue.firestore
        .collection('customers')
        .doc(this.customerId)
        .collection('locations')
        .doc(this.locationId)
        .collection('patients')
    },
    buildBasePatientsQuery() {
      const collectionRef = this.patientsCollectionRef()
      if (!collectionRef) {
        return null
      }

      let query = collectionRef

      if (this.selectedStageFilter !== 'all') {
        query = query.where('pathToHealthStageNum', '==', Number(this.selectedStageFilter))
      }

      return query
    },
    mapPatientDoc(doc) {
      const data = doc.data() || {}
      return {
        id: doc.id,
        patnum: data.patnum,
        patientName: data.patientName || '',
        priorityScore: data.priorityScore,
        lastContactedDate: data.lastContactedDate || null,
        mostRecentAppointmentDate: data.mostRecentAppointmentDate || null,
        stage: data.pathToHealthStageNum || null,
        pathToHealthStageIsScheduled: data.pathToHealthStageIsScheduled,
        activeCaseId: data.activeCaseId || null,
        hasCompletedCase: data.hasCompletedCase === true,
        completedAt: data.completedAt || null,
        activeCaseAssignedToId: null,
        activeCaseAssignmentLoaded: false,
        snoozedUntil: data.snoozedUntil || null,
      }
    },
    sortPatients(patients) {
      return [...patients].sort((left, right) => {
        const leftScore = this.toSortablePriorityScore(left.priorityScore)
        const rightScore = this.toSortablePriorityScore(right.priorityScore)

        const leftHasScore = leftScore !== null
        const rightHasScore = rightScore !== null

        if (leftHasScore !== rightHasScore) {
          return leftHasScore ? -1 : 1
        }

        if (leftHasScore && rightHasScore && leftScore !== rightScore) {
          return rightScore - leftScore
        }

        const leftUpdated = Number(left.lastUpdatedTimestamp) || 0
        const rightUpdated = Number(right.lastUpdatedTimestamp) || 0

        if (leftUpdated !== rightUpdated) {
          return rightUpdated - leftUpdated
        }

        return String(left.id || '').localeCompare(String(right.id || ''))
      })
    },
    toSortablePriorityScore(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    },
    toUnixTimestamp(value) {
      if (!value) {
        return null
      }
      if (typeof value === 'object' && Number.isFinite(value.seconds)) {
        return value.seconds
      }

      const timestamp = Number(value)
      return Number.isFinite(timestamp) ? timestamp : null
    },
    isSnoozed(patient) {
      return this.toUnixTimestamp(patient.snoozedUntil) > moment.tz('America/Chicago').unix()
    },
    isCompleted(patient) {
      return patient.hasCompletedCase === true && !patient.activeCaseId
    },
    isScheduled(patient) {
      return patient.pathToHealthStageIsScheduled === true
    },
    matchesPatientListMode(patient) {
      if (this.patientListMode === 'all') {
        return true
      }

      if (this.patientListMode === 'snoozed') {
        return this.isSnoozed(patient) && !this.isCompleted(patient)
      }

      if (this.patientListMode === 'completed') {
        return this.isCompleted(patient)
      }

      if (this.patientListMode === 'scheduled') {
        return this.isScheduled(patient) && !this.isSnoozed(patient) && !this.isCompleted(patient)
      }

      return !this.isSnoozed(patient) && !this.isCompleted(patient) && !this.isScheduled(patient)
    },
    matchesDateFilter(value, filter) {
      if (filter === 'all') {
        return true
      }

      const timestamp = this.toUnixTimestamp(value)
      if (!timestamp) {
        return false
      }

      const now = moment.tz('America/Chicago')
      if (filter === 'yesterday') {
        const start = now.clone().subtract(1, 'day').startOf('day').unix()
        const end = now.clone().startOf('day').unix()
        return timestamp >= start && timestamp < end
      }

      const rangeUnits = {
        oneWeek: ['week', 1],
        oneMonth: ['month', 1],
        sixMonths: ['months', 6],
        oneYear: ['year', 1],
        twoYears: ['years', 2],
      }
      const [unit, amount] = rangeUnits[filter] || []
      return Boolean(unit) && timestamp >= now.clone().subtract(amount, unit).unix() && timestamp <= now.unix()
    },
    async loadPage({ pageNumberToSet = null } = {}) {
      this.loading = true
      this.errors = []
      let resultCount = 0

      try {
        const baseQuery = this.buildBasePatientsQuery()
        if (!baseQuery) {
          this.patients = []
          this.allPatients = []
          this.hasNextPage = false
          return resultCount
        }

        const snapshot = await baseQuery.get()
        const docs = snapshot.docs || []
        this.allPatients = this.sortPatients(docs.map((doc) => this.mapPatientDoc(doc)))

        if (pageNumberToSet !== null) {
          this.pageNumber = pageNumberToSet
        }

        this.applyCurrentPage()

        resultCount = this.allPatients.length
      } catch (err) {
        console.error('Failed to load patients:', err)
        this.errors = ['Failed to load patients. Please try again.']
      } finally {
        this.loading = false
      }

      return resultCount
    },
    async resetAndLoad() {
      this.clearBatchSelection()
      this.patients = []
      this.pageNumber = 1
      this.hasNextPage = false

      await this.loadPage({ pageNumberToSet: 1 })
    },
    applyCurrentPage() {
      const startIndex = (this.pageNumber - 1) * PAGE_SIZE
      const endIndex = startIndex + PAGE_SIZE
      this.patients = this.filteredPatients.slice(startIndex, endIndex)
      this.hasNextPage = endIndex < this.filteredPatients.length
    },
    async onStageFilterChange() {
      await this.resetAndLoad()
    },
    onScheduledFilterChange() {
      this.onClientFilterChange()
    },
    onClientFilterChange() {
      this.clearBatchSelection()
      this.pageNumber = 1
      this.applyCurrentPage()
    },
    clearBatchSelection() {
      this.selectedPatientIds = []
      this.batchAssignedTeamMemberId = ''
    },
    toggleAllVisiblePatients(event) {
      const visiblePatientIds = this.patients.map((patient) => patient.id)
      if (event.target.checked) {
        this.selectedPatientIds = Array.from(new Set([...this.selectedPatientIds, ...visiblePatientIds]))
      } else {
        this.selectedPatientIds = this.selectedPatientIds.filter((id) => !visiblePatientIds.includes(id))
      }
    },
    async batchAssignPatients() {
      if (!this.selectedPatientIds.length || !this.batchAssignedTeamMemberId || this.batchAssigning) {
        return
      }

      this.batchAssigning = true
      this.errors = []

      try {
        const batchAssign = Vue.functions.httpsCallable('batchAssignPatients')
        await batchAssign({
          patientIds: this.selectedPatientIds,
          teamMemberId: this.batchAssignedTeamMemberId,
        })
        await this.resetAndLoad()
      } catch (err) {
        console.error('Failed to batch assign patients:', err)
        this.errors = ['Failed to assign patients. Please try again.']
      } finally {
        this.batchAssigning = false
      }
    },
    async onAssignedTeamMemberChange() {
      if (this.selectedAssignedTeamMemberId) {
        await this.loadActiveCaseAssignments()
      }
      this.onClientFilterChange()
    },
    async loadActiveCaseAssignments() {
      const patientsRef = this.patientsCollectionRef()
      if (!patientsRef) {
        return
      }

      const activeCasePatients = this.allPatients.filter((patient) => patient.activeCaseId && !patient.activeCaseAssignmentLoaded)
      await Promise.all(activeCasePatients.map(async (patient) => {
        try {
          const activeCaseDoc = await patientsRef.doc(patient.id).collection('cases').doc(patient.activeCaseId).get()
          const activeCase = activeCaseDoc.data() || {}
          this.$set(patient, 'activeCaseAssignedToId', activeCaseDoc.exists && activeCase.status === 'open' ? activeCase.assignedToId || '' : '')
          this.$set(patient, 'activeCaseAssignmentLoaded', true)
        } catch (err) {
          console.error('Failed to load active patient case assignment:', err)
        }
      }))
    },
    async loadNextPage() {
      if (!this.hasNextPage) {
        return
      }

      this.pageNumber += 1
      this.applyCurrentPage()
    },
    async loadPreviousPage() {
      if (this.pageNumber <= 1) {
        return
      }

      this.pageNumber -= 1
      this.applyCurrentPage()
    },
    csvEscape(value) {
      const stringValue = value === null || value === undefined ? '' : String(value)
      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    },
    goToPatientDetail(patient) {
      if (!patient.patnum) {
        return
      }
      this.$router.push({ name: 'patientDetail', params: { patNum: patient.patnum } })
    },
    downloadCSV() {
      // filteredPatients already holds the full filtered result set across all pages
      const columns = ['Priority', 'Patient Name', 'Patnum', 'Stage', 'Scheduled', 'Last Updated']
      const rows = this.filteredPatients.map((patient) => [
        patient.priorityScore || patient.priorityScore === 0 ? Math.round(patient.priorityScore) : '',
        patient.patientName || '',
        patient.patnum || '',
        patient.stage || '',
        patient.pathToHealthStageIsScheduled === true ? 'Yes' : patient.pathToHealthStageIsScheduled === false ? 'No' : '',
        patient.lastUpdatedTimestamp ? Vue.filter('prettyDateTimeShortTz')(patient.lastUpdatedTimestamp) : '',
      ])

      const csvContent = [columns, ...rows]
        .map((row) => row.map((cell) => this.csvEscape(cell)).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `patient-list-${Date.now()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  },
  mounted() {
    this.resetAndLoad()
  },
}
</script>
<style lang="less" scoped>
@import "../assets/less/variables.less";

.col-pt-list > hr:first-child {
  margin-bottom: 30px;
}
.form-row label {
  text-transform: uppercase;
  font-size: 12px;
  line-height: 12px;
  font-weight: bold;
}
.stage-icon {
  margin-right: 5px;
}
.btn-patient-list svg + span {
  vertical-align: middle;
}
.patient-list-responsive {
  color: @dark-color;
  overflow-x: auto;
  width: 100%;
}
.patient-list-header,
.patient-list-row,
// .btn-patient-list,
.patient-list-empty,
.patient-list-container {
  min-width: 1000px;
}
.patient-list-row {
  display: flex;
}
.patient-list-checkbox-cell {
  flex: 0 0 38px;
  padding-left: 0;
  padding-right: 0;
  text-align: center;
}
.patient-list-checkbox-cell input {
  width: 16px;
  height: 16px;
}
.patient-list-thead {
  flex: 1 1 auto;
  width: auto;
  min-width: 760px;
}
.btn-patient-list {
  // color: inherit;
  // font: inherit;
  cursor: pointer;
  flex: 1 1 auto;
  width: auto;
  min-width: 760px;
  padding: 0;
  background-color: transparent;
  transition: none;
}
.patient-list-row:nth-of-type(odd) .btn-patient-list {
  background-color: #FAFAFA;
}
.patient-list-row:last-child {
  border-bottom: 1px solid @dim-color;
}
.btn-patient-list:hover {
  transition: none;
  background-color: fadeout(@color-blue,50%) !important;
}
.btn-patient-list:focus-visible {
  outline: 2px solid @color-blue;
  outline-offset: -2px;
}
.patient-list-cell {
  text-align: left;
  padding: 12px;
}
.patient-list-header .patient-list-cell {
  font-weight: bold;
  // border-bottom: 1px solid #a7ebff;
}
.patient-list-header .patient-list-cell:after {
    content: "";
    display: block;
    width: 100%;
    border-bottom: 1px solid @color-blue;
    padding-top: 10px;
  }
.patient-list-empty {
  padding: 20px;
}
.row-table-footer {
  margin-top: 30px;
  margin-bottom: 30px;
  // border-top: 1px solid @dim-color;
  border-bottom: 1px solid @dark-color;
  // padding-top: 15px;
  padding-bottom: 30px;

  // > div {
  //   padding: 0;
  // }
}
.batch-assign-form {
  p {
    margin-bottom: 5px;
  }
  .form-control {
    margin: 0;
  }
  .flex-grow-1 {
    margin-right: 10px;
  }
}
.h-scroll-indicator {
  text-align: right;
  margin: 20px 0;
  font-size: 12px;
  color: @dark-color;
}

// only show scroll indicator if viewport results in table being less than 1000px wide
@media (min-width: 1200px) {
  .h-scroll-indicator {
    display: none;
  }
}
// show H scrollbar at top instead of bottom for small screens
@media (max-width: 1200px) {
  .table-responsive {
    overflow-x: auto;
    overflow-y: hidden;
    transform: rotateX(180deg);
  }
  .patient-list-container {
    transform: rotateX(180deg);
  }
}



</style>
